import type { SoapDraftProvenanceResponse } from "../../../shared/api-types";

/**
 * "1:53 PM" today, "Aug 7 · 1:53 PM" on any other day. The footer sits next
 * to a header that already names the visit date, so same-day times drop it.
 */
export function clockOrDateLabel(
  value: string,
  input: { now: string; timezone: string }
): string {
  const date = new Date(value);
  const time = new Intl.DateTimeFormat("en-US", {
    timeZone: input.timezone,
    hour: "numeric",
    minute: "2-digit",
  }).format(date);

  if (calendarDate(value, input.timezone) === calendarDate(input.now, input.timezone)) {
    return time;
  }

  return `${monthDayLabel(value, input.timezone)} · ${time}`;
}

export function monthDayLabel(value: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    month: "short",
    day: "numeric",
  }).format(new Date(value));
}

/**
 * The provenance sentence under the draft: what it was generated from, in the
 * RD's words. "Draft generated 1:53 PM from today's transcript, your
 * appointment note, and the Jul 24 signed note." Optional inputs the
 * snapshot did not record are simply absent from the sentence.
 *
 * NO-775: a pasted transcript reads "a pasted transcript" and a pasted prior
 * note "a pasted prior note". Once a newer Healthie transcript is current for
 * a draft made from a pasted one, `notice` says so; the draft never changes.
 */
export function draftProvenanceLine(
  provenance: SoapDraftProvenanceResponse,
  input: { now: string; timezone: string; newerHealthieTranscript?: boolean }
): { generated: string; sources: string; notice: string | null } {
  const pastedTranscript = provenance.transcriptSource === "rd_pasted";
  const sources = [pastedTranscript ? "a pasted transcript" : "today’s transcript"];
  if (provenance.usedAppointmentNote) sources.push("your appointment note");

  const priorNote = priorNoteSource(provenance, input.timezone);
  if (priorNote !== null) sources.push(priorNote);

  return {
    generated: `Draft generated ${clockOrDateLabel(provenance.generatedAt, input)}`,
    sources: ` from ${joinSources(sources)}.`,
    notice:
      pastedTranscript && input.newerHealthieTranscript === true
        ? "A newer transcript arrived from Healthie."
        : null,
  };
}

function priorNoteSource(
  provenance: SoapDraftProvenanceResponse,
  timezone: string
): string | null {
  if (provenance.priorNoteSource === "rd_pasted") return "a pasted prior note";

  if (provenance.priorSignedNoteAt === null) return null;

  return `the ${monthDayLabel(provenance.priorSignedNoteAt, timezone)} signed note`;
}

function joinSources(sources: string[]): string {
  if (sources.length <= 1) return sources.join("");

  if (sources.length === 2) return `${sources[0]} and ${sources[1]}`;

  return `${sources.slice(0, -1).join(", ")}, and ${sources[sources.length - 1]}`;
}

function calendarDate(value: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(value));
}
